import React from 'react';
import { Link } from 'react-router-dom';
import LayoutRenderer from './layouts/LayoutRenderer';
import { BACKEND_URL as API } from '../lib/config';

// ─────────────────────────────────────────────────────────────────────────
// BrandPage — interior CMS pages (DynamicPage) in the home's own design for
// Personal Brand Pro (Carlos) and Private Community Pro (Aurex). Same scheme
// as ModernPage (Acapital, s140): a dark page header with breadcrumb + title
// that the navbar floats over (like each home's hero), then a white reading
// column. Each skin only swaps fonts and the brand's CMS colours.
// ─────────────────────────────────────────────────────────────────────────

const img = (s) => (s && s.startsWith('/api') ? `${API}${s}` : s);

const SKINS = {
  personalbrand: {
    body: "'Plus Jakarta Sans', 'Inter', sans-serif",
    head: "'Plus Jakarta Sans', 'Inter', sans-serif",
    titleWeight: 900,
    headWeight: 800,
    headerBg: 'var(--color-footer-bg, #111111)',
    overlay: 'linear-gradient(180deg, rgba(10,10,10,.55) 0%, rgba(10,10,10,.85) 100%)',
    rule: 'var(--color-accent, #C9A24B)',
    shell: 'max-w-7xl mx-auto px-6 sm:px-10 md:px-16 lg:px-24',
  },
  privatecommunity: {
    body: "'Inter', sans-serif",
    head: "'Sora', 'Inter', sans-serif",
    titleWeight: 800,
    headWeight: 700,
    headerBg: 'var(--color-primary, #1a2332)',
    overlay: 'linear-gradient(90deg, rgba(17,24,39,.92) 0%, rgba(17,24,39,.7) 55%, rgba(17,24,39,.4) 100%)',
    rule: 'var(--color-accent, #73af5f)',
    shell: 'max-w-7xl mx-auto px-6 md:px-10',
  },
};

const css = (s) => `
.bp-prose{font-family:${s.body};font-size:1.0625rem;line-height:1.75;color:var(--color-body-text,#475569);overflow-wrap:break-word;text-wrap:pretty}
.bp-prose p{margin:0 0 1.1em}
.bp-prose strong,.bp-prose b{color:var(--color-heading,#1a2332)}
.bp-prose a{color:var(--color-heading,#1a2332);text-decoration:underline;text-decoration-color:${s.rule};text-decoration-thickness:2px;text-underline-offset:.25em;transition:text-decoration-color .2s ease-out}
.bp-prose a:hover{text-decoration-color:currentColor}
.bp-prose h1,.bp-prose h2,.bp-prose h3,.bp-prose h4{font-family:${s.head};font-weight:${s.headWeight};letter-spacing:-0.01em;color:var(--color-heading,#1a2332)!important;line-height:1.25;text-wrap:balance}
.bp-prose>*:first-child{margin-top:0}
.bp-prose ul li::marker,.bp-prose ol li::marker{color:${s.rule}}
.bp-prose th{color:var(--color-heading,#1a2332);font-family:${s.head}}
.bp-prose img{max-width:100%;height:auto}
@keyframes bp-rise{from{transform:translateY(14px)}to{transform:none}}
.bp-rise{animation:bp-rise .7s cubic-bezier(.22,1,.36,1)}
@media (prefers-reduced-motion:reduce){.bp-rise{animation:none}}
`;

function Header({ page, s }) {
  const [broken, setBroken] = React.useState(false);
  const src = broken ? null : img(page.banner_image);
  return (
    // data-testid="hero-section" lets the navbar float transparent over it, as on the home.
    <header data-testid="hero-section" className="relative isolate overflow-hidden flex items-end min-h-[340px] lg:min-h-[420px]" style={{ background: s.headerBg }}>
      {src && <img src={src} alt="" onError={() => setBroken(true)} className="absolute inset-0 -z-10 w-full h-full object-cover" />}
      {src && <span className="absolute inset-0 -z-10" style={{ background: s.overlay }} />}
      <div className={`${s.shell} w-full pt-32 pb-12 lg:pb-16 bp-rise`}>
        <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-2 text-[13px] font-semibold mb-5" style={{ color: 'rgba(255,255,255,.65)' }}>
          <Link to="/" className="py-3 -my-3 sm:py-0 sm:my-0 transition-colors duration-200 hover:text-white">Home</Link>
          <span aria-hidden="true">/</span>
          <span className="truncate" style={{ color: 'rgba(255,255,255,.9)' }}>{page.title}</span>
        </nav>
        <h1 className="text-white max-w-4xl" style={{ fontFamily: s.head, fontWeight: s.titleWeight, fontSize: 'clamp(2.25rem, 5.2vw, 4.5rem)', lineHeight: 1.04, letterSpacing: '-0.02em', textWrap: 'balance' }} data-testid="page-title">
          {page.title}
        </h1>
        <span aria-hidden="true" className="block w-10 h-[2px] mt-6" style={{ background: s.rule }} />
        {page.summary && <p className="mt-5 text-base md:text-lg max-w-2xl" style={{ color: 'rgba(255,255,255,.78)', lineHeight: 1.7 }}>{page.summary}</p>}
      </div>
    </header>
  );
}

export default function BrandPage({ page, theme }) {
  const s = SKINS[theme] || SKINS.privatecommunity;
  return (
    <div data-testid="dynamic-page" style={{ background: 'var(--color-bg, #ffffff)', fontFamily: s.body }}>
      <style>{css(s)}</style>
      <Header page={page} s={s} />
      {page.layout
        ? <LayoutRenderer page={page} hasHero hideTitle />
        : (
          <div className={`${s.shell} py-14 lg:py-20`}>
            {page.content && <div className="bp-prose max-w-[72ch]" dangerouslySetInnerHTML={{ __html: page.content }} />}
          </div>
        )}
    </div>
  );
}

export const BRAND_PAGE_THEMES = Object.keys(SKINS);
